import { useState, useCallback } from "react";

interface ToastState {
  isVisible: boolean;
  isSuccess: boolean;
  isLoading?: boolean;
  title: string;
  message: string;
}

interface UseToastReturn {
  toast: ToastState;
  showSuccess: (message: string, title?: string) => void;
  showError: (message: string, title?: string) => void;
  showLoading: (message: string, title?: string) => void;
  hide: () => void;
}

/**
 * Hook for managing toast notification state
 */
export const useToast = (): UseToastReturn => {
  const [toast, setToast] = useState<ToastState>({
    isVisible: false,
    isSuccess: true,
    isLoading: false,
    title: "",
    message: "",
  });

  const showSuccess = useCallback(
    (message: string, title: string = "Success") => {
      setToast({
        isVisible: true,
        isSuccess: true,
        isLoading: false,
        title,
        message,
      });
    },
    [],
  );

  const showError = useCallback((message: string, title: string = "Error") => {
    setToast({
      isVisible: true,
      isSuccess: false,
      isLoading: false,
      title,
      message,
    });
  }, []);

  const showLoading = useCallback(
    (message: string, title: string = "Loading") => {
      setToast({
        isVisible: true,
        isSuccess: true,
        isLoading: true,
        title,
        message,
      });
    },
    [],
  );

  const hide = useCallback(() => {
    setToast((prev) => ({ ...prev, isVisible: false }));
  }, []);

  return {
    toast,
    showSuccess,
    showError,
    showLoading,
    hide,
  };
};
